import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControlLabel,
  Grow,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  Search as SearchIcon,
  VisibilityOff as HideIcon,
  Visibility as ShowIcon,
  CardGiftcard as AwardIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  Circle as DotIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Casino as SpinIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Stop as EndIcon,
} from '@mui/icons-material';
import io from 'socket.io-client';
import LiveWheelDisplay from '../../Common/LiveWheelDisplay';
import PrizeRevealOverlay from '../../Common/PrizeRevealOverlay';
import usePrizeWheelSpinAudio from '../../../hooks/usePrizeWheelSpinAudio';
import usePrizePaletteEventAudio from '../../../hooks/usePrizePaletteEventAudio';
import { entryFromSpin, prizeIndexFromSpin, sliceSignature } from '../../Common/DefaultStyleWheel';
import {
  API_BASE,
  PRIZE_GAMES,
  authHeaders,
  mediaUrl,
  prizeGameLabel,
  prizeInStock,
  prizeIsCollage,
  prizeIsWheel,
  prizeIsUnlimited,
  prizeMatchesCap,
  prizeValueLabel,
} from './prizePaletteShared';

// react-custom-roulette rebuilds its spin keyframes on every render.
// Keep this face from re-rendering while the desk polls players.
const StableLiveWheel = React.memo(LiveWheelDisplay);

const LIVE_AWARD_FILTERS_KEY = 'prize_live_award_filters';

const readLiveAwardFilters = () => {
  try {
    const raw = sessionStorage.getItem(LIVE_AWARD_FILTERS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const PrizeLiveAward = () => {
  const savedFilters = useRef(readLiveAwardFilters());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [players, setPlayers] = useState([]);
  const [showTime, setShowTime] = useState(null);
  const [shownAwards, setShownAwards] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState(savedFilters.current.categoryFilter || 'all');
  const [tierCap, setTierCap] = useState(
    savedFilters.current.tierCap == null || savedFilters.current.tierCap === ''
      ? null
      : Number(savedFilters.current.tierCap)
  );
  const [search, setSearch] = useState(savedFilters.current.search || '');
  const [playerId, setPlayerId] = useState('');
  const [prizeId, setPrizeId] = useState(null);
  const [finderOpen, setFinderOpen] = useState(false);
  const [finderStep, setFinderStep] = useState('name');
  const [finderName, setFinderName] = useState('');
  const [finderConfirm, setFinderConfirm] = useState('');
  const [finderMatches, setFinderMatches] = useState([]);
  const [finderBusy, setFinderBusy] = useState(false);
  const [playerPickerOpen, setPlayerPickerOpen] = useState(false);
  const [playerFilter, setPlayerFilter] = useState(savedFilters.current.playerFilter || '');
  const [gameKey, setGameKey] = useState(
    PRIZE_GAMES.some((game) => game.id === savedFilters.current.gameKey)
      ? savedFilters.current.gameKey
      : 'all'
  );
  const [liveScreen, setLiveScreen] = useState(null);
  const [paletteAudioReady, setPaletteAudioReady] = useState(false);
  const [topBarOpen, setTopBarOpen] = useState(false);
  const [bottomBarOpen, setBottomBarOpen] = useState(false);
  const playerGraceRef = useRef(new Map()); // session_id -> { player, missingSince }
  const lastTapRef = useRef({ id: null, at: 0 });
  const previewRef = useRef(null);

  // Wheel modal state
  const [wheelModalOpen, setWheelModalOpen] = useState(false);
  const [wheelSession, setWheelSession] = useState(null);
  const [wheelEntries, setWheelEntries] = useState([]);
  const [wheelMeta, setWheelMeta] = useState(null);
  const [wheelSpinning, setWheelSpinning] = useState(false);
  const [wheelFinalAngle, setWheelFinalAngle] = useState(0);
  const [wheelSpinDuration, setWheelSpinDuration] = useState(6);
  const [wheelWinner, setWheelWinner] = useState(null);
  const [wheelAnimComplete, setWheelAnimComplete] = useState(false);
  const [wheelPrizeIndex, setWheelPrizeIndex] = useState(0);
  const [wheelPlayerCanSpin, setWheelPlayerCanSpin] = useState(false);
  const [wheelPlayerUnlocked, setWheelPlayerUnlocked] = useState(false);
  const [wheelBusy, setWheelBusy] = useState(false);
  const [wheelHold, setWheelHold] = useState(false);
  const wheelSpinningRef = useRef(false);
  const wheelEntriesRef = useRef([]);
  const wheelSessionIdRef = useRef(null);
  const wheelWinnerRef = useRef(null);
  const adminSpinPendingRef = useRef(false);
  const { playSpinStart, playWinnerClap } = usePrizeWheelSpinAudio();
  const prizeAudioRef = useRef({ playSpinStart, playWinnerClap });
  prizeAudioRef.current = { playSpinStart, playWinnerClap };

  useEffect(() => {
    try {
      sessionStorage.setItem(LIVE_AWARD_FILTERS_KEY, JSON.stringify({
        categoryFilter,
        tierCap,
        search,
        playerFilter,
        gameKey,
      }));
    } catch {
      /* private mode or full storage */
    }
  }, [categoryFilter, tierCap, search, playerFilter, gameKey]);

  useEffect(() => {
    if (loading) return;
    if (
      categoryFilter !== 'all'
      && !categories.some((cat) => String(cat.id) === String(categoryFilter))
    ) {
      setCategoryFilter('all');
    }
    if (
      tierCap != null
      && !tiers.some((tier) => Number(tier.cap_amount) === Number(tierCap))
    ) {
      setTierCap(null);
    }
  }, [loading, categories, tiers, categoryFilter, tierCap]);

  const applyPlayers = useCallback((incoming) => {
    const now = Date.now();
    const graceMs = 60 * 1000;
    const nextIds = new Set((incoming || []).map((p) => p.session_id));
    const grace = playerGraceRef.current;

    (incoming || []).forEach((player) => {
      grace.set(player.session_id, { player, missingSince: null });
    });

    grace.forEach((entry, sessionId) => {
      if (nextIds.has(sessionId)) return;
      if (entry.missingSince == null) {
        entry.missingSince = now;
        entry.player = { ...entry.player, status: 'away' };
      } else if (now - entry.missingSince >= graceMs) {
        grace.delete(sessionId);
      }
    });

    const merged = [];
    grace.forEach((entry) => {
      merged.push(entry.player);
    });
    merged.sort((a, b) => String(a.username || '').localeCompare(String(b.username || '')));
    setPlayers(merged);
  }, []);

  const loadPlayers = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/prize-palette/players`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) return;
      applyPlayers(data.players || []);
      setShowTime(data.showTime || null);
      setLiveScreen(data.screen || null);
      setPaletteAudioReady(true);
    } catch (err) {
      console.error('live players load', err);
    }
  }, [applyPlayers]);

  const load = useCallback(async () => {
    try {
      const [catalogRes, tierRes, playerRes, awardRes] = await Promise.all([
        fetch(`${API_BASE}/api/prize-palette/categories?withPrizes=true&includeDisabled=false`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/prize-palette/tiers?includeDisabled=false`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/prize-palette/players`, { headers: authHeaders() }),
        fetch(`${API_BASE}/api/prize-palette/awards?status=shown`, { headers: authHeaders() }),
      ]);
      const [catalog, tierData, playerData, awardData] = await Promise.all([
        catalogRes.json(), tierRes.json(), playerRes.json(), awardRes.json(),
      ]);
      if (!catalogRes.ok || !catalog.success) { setError(catalog.error || 'Failed to load prizes'); return; }
      setCategories(catalog.categories || []);
      setTiers(tierData.tiers || []);
      applyPlayers(playerData.players || []);
      setShowTime(playerData.showTime || null);
      setLiveScreen(playerData.screen || null);
      setPaletteAudioReady(true);
      setShownAwards(awardData.awards || []);
      setError(null);
    } catch (err) {
      console.error('live prize load', err);
      setError('Failed to load the live prize desk');
    } finally { setLoading(false); }
  }, [applyPlayers]);

  useEffect(() => {
    load();
    const catalogPoll = setInterval(load, 8000);
    const playerPoll = setInterval(loadPlayers, 1500);
    const socket = io(API_BASE, { withCredentials: true });
    socket.on('connect', () => {
      socket.emit('prize:admin-join');
      socket.emit('admin:join-active-users');
      socket.emit('spinwheel:join-live');
    });
    socket.on('prize:changed', () => load());
    socket.on('active-user:update', () => loadPlayers());

    // Player-initiated spins. Admin spins are applied from the REST response so
    // the wheel receives angle, index, and winner in a single render.
    socket.on('spinwheel:hot_seat_spin_start', ({ finalAngle, spinDuration, winnerIdx, winnerId }) => {
      if (adminSpinPendingRef.current) return;
      const list = wheelEntriesRef.current;
      const winner = entryFromSpin(winnerIdx, winnerId, list);
      wheelSpinningRef.current = true;
      wheelWinnerRef.current = winner;
      setWheelPrizeIndex(prizeIndexFromSpin(winnerIdx, winnerId, list));
      setWheelFinalAngle(finalAngle);
      setWheelSpinDuration(spinDuration || 6);
      setWheelAnimComplete(false);
      setWheelHold(false);
      setWheelWinner(winner);
      setWheelSpinning(true);
      prizeAudioRef.current.playSpinStart();
    });
    socket.on('spinwheel:hot_seat_spin_complete', ({ winner }) => {
      if (!winner) return;
      wheelWinnerRef.current = winner;
      setWheelWinner(winner);
    });
    socket.on('spinwheel:hot_seat_updated', ({ session }) => {
      if (session) {
        setWheelPlayerCanSpin(!!session.player_can_spin);
        setWheelPlayerUnlocked(!!session.player_spin_unlocked);
      }
    });

    return () => {
      clearInterval(catalogPoll);
      clearInterval(playerPoll);
      socket.emit('prize:admin-leave');
      socket.emit('admin:leave-active-users');
      socket.disconnect();
    };
  }, [load, loadPlayers]);

  useEffect(() => {
    if (adminSpinPendingRef.current) return;
    wheelSpinningRef.current = wheelSpinning;
  }, [wheelSpinning]);
  useEffect(() => {
    wheelEntriesRef.current = wheelEntries;
  }, [wheelEntries]);

  const openWheelModal = async (prize, player) => {
    if (!prize || !player || !prizeIsWheel(prize)) return;
    setWheelBusy(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/prize-palette/wheel-session/start`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          prize_id: prize.id,
          session_id: player.session_id,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to start wheel session');
        return;
      }
      wheelSessionIdRef.current = data.session?.id || null;
      wheelWinnerRef.current = null;
      wheelSpinningRef.current = false;
      adminSpinPendingRef.current = false;
      setWheelSession(data.session);
      setWheelEntries(data.session?.entries || []);
      setWheelMeta(data.wheel || null);
      setWheelWinner(null);
      setWheelSpinning(false);
      setWheelHold(false);
      setWheelAnimComplete(false);
      setWheelPrizeIndex(0);
      setWheelPlayerCanSpin(!!data.session?.player_can_spin);
      setWheelPlayerUnlocked(!!data.session?.player_spin_unlocked);
      setWheelModalOpen(true);
    } catch (err) {
      console.error('openWheelModal', err);
      setError('Failed to start wheel session');
    } finally {
      setWheelBusy(false);
    }
  };

  const handleWheelSpin = async () => {
    const sessionId = wheelSessionIdRef.current || wheelSession?.id;
    if (!sessionId || wheelSpinningRef.current) return;
    adminSpinPendingRef.current = true;
    wheelSpinningRef.current = true;
    setWheelBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/prize-palette/wheel-session/${sessionId}/spin`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        wheelSpinningRef.current = false;
        setError(data.error || 'Spin failed');
        return;
      }
      const list = wheelEntriesRef.current;
      const winner = data.winner || null;
      wheelWinnerRef.current = winner;
      setWheelPrizeIndex(prizeIndexFromSpin(data.winnerIdx, winner?.id || winner?.slice_id, list));
      setWheelFinalAngle(data.finalAngle);
      setWheelSpinDuration(data.spinDuration || 6);
      setWheelAnimComplete(false);
      setWheelHold(false);
      setWheelWinner(winner);
      setWheelSpinning(true);
      prizeAudioRef.current.playSpinStart();
      if (data.session) {
        setWheelSession((prev) => ({ ...(prev || {}), ...data.session, id: sessionId }));
      }
    } catch (err) {
      wheelSpinningRef.current = false;
      console.error('handleWheelSpin', err);
      setError('Spin failed');
    } finally {
      adminSpinPendingRef.current = false;
      setWheelBusy(false);
    }
  };

  const handleWheelAnimEnd = useCallback(() => {
    wheelSpinningRef.current = false;
    setWheelSpinning(false);
    setWheelHold(false);
    setWheelAnimComplete(true);
    if (wheelWinnerRef.current) setWheelWinner(wheelWinnerRef.current);
  }, []);

  const markWheelLanded = useCallback(() => setWheelHold(true), []);
  const revealWheelPrize = useCallback(() => {
    prizeAudioRef.current.playWinnerClap();
  }, []);

  const patchWheelSettings = async (settings) => {
    if (!wheelSession) return;
    setWheelBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/prize-palette/wheel-session/${wheelSession.id}/settings`, {
        method: 'PATCH',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(settings),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to update settings');
        return;
      }
      if (data.session) {
        const sessionId = wheelSessionIdRef.current || data.session.id;
        setWheelSession((prev) => ({ ...(prev || {}), ...data.session, id: sessionId }));
        if (!wheelSpinningRef.current) {
          const nextEntries = data.session.entries || wheelEntriesRef.current;
          setWheelEntries((prev) => (
            sliceSignature(prev) === sliceSignature(nextEntries) ? prev : nextEntries
          ));
        }
        setWheelPlayerCanSpin(!!data.session.player_can_spin);
        setWheelPlayerUnlocked(!!data.session.player_spin_unlocked);
      }
    } catch (err) {
      console.error('patchWheelSettings', err);
    } finally {
      setWheelBusy(false);
    }
  };

  const closeWheelModal = async () => {
    const sessionId = wheelSessionIdRef.current || wheelSession?.id;
    if (sessionId) {
      setWheelBusy(true);
      try {
        const winner = wheelWinnerRef.current;
        const res = await fetch(`${API_BASE}/api/prize-palette/wheel-session/${sessionId}/end`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            winner_slice_id: winner?.slice_id || winner?.id || null,
            winner_text: winner?.display_name || winner?.text || '',
          }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          setError(data.error || 'Failed to save this wheel result');
          return;
        }
        if (winner && !data.award) {
          setError('Spin finished, but it was not saved to prize history');
          return;
        }
        try {
          await fetch(`${API_BASE}/api/prize-palette/screen/hide`, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({}),
          });
        } catch (hideErr) {
          console.error('hide after wheel close', hideErr);
        }
        if (winner) {
          setPlayerId('');
          setPrizeId(null);
        }
        load();
      } catch (err) {
        console.error('closeWheelModal end', err);
        setError('Failed to save this wheel result');
        return;
      } finally {
        setWheelBusy(false);
      }
    }
    wheelSessionIdRef.current = null;
    wheelWinnerRef.current = null;
    wheelSpinningRef.current = false;
    setWheelModalOpen(false);
    setWheelSession(null);
    setWheelEntries([]);
    setWheelMeta(null);
    setWheelWinner(null);
    setWheelSpinning(false);
    setWheelAnimComplete(false);
    setWheelPlayerCanSpin(false);
    setWheelPlayerUnlocked(false);
  };

  const prizes = useMemo(() => {
    const query = search.trim().toLowerCase();
    const rows = [];
    categories.forEach((cat) => {
      if (categoryFilter !== 'all' && String(cat.id) !== String(categoryFilter)) return;
      (cat.prizes || []).forEach((prize) => {
        if (!prize.enabled) return;
        if (query && !prize.name.toLowerCase().includes(query)) return;
        if (!prizeMatchesCap(prize, tierCap)) return;
        rows.push({ ...prize, category_name: cat.name, category_color: cat.color });
      });
    });
    return rows;
  }, [categories, categoryFilter, search, tierCap]);

  const filteredPlayers = useMemo(() => {
    const q = playerFilter.trim().toLowerCase();
    if (!q) return players;
    return players.filter((p) => {
      const hay = [p.username, p.email, p.guest_id, p.current_page_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [players, playerFilter]);

  const selectedPrize = prizes.find((p) => p.id === prizeId) || null;
  const selectedIsCollage = prizeIsCollage(selectedPrize);
  const selectedIsWheel = prizeIsWheel(selectedPrize);
  const selectedPlayer = players.find((p) => p.session_id === playerId) || null;
  const openShow = shownAwards.find((a) => a.session_id === playerId) || null;

  const isPrizeOnScreen = useCallback((prize) => {
    if (!prize || !liveScreen) return false;
    if (liveScreen.kind !== 'display' && liveScreen.kind !== 'collage') return false;
    return Number(liveScreen.prize_id) === Number(prize.id);
  }, [liveScreen]);

  const act = async (path, body) => {
    try {
      setBusy(true); setError(null);
      const res = await fetch(`${API_BASE}/api/prize-palette/${path}`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) { setError(data.error || 'That action did not go through'); return false; }
      await load();
      return true;
    } catch (err) { console.error(path, err); setError('That action did not go through'); return false; }
    finally { setBusy(false); }
  };

  const showPrize = (prize = selectedPrize) => {
    if (!prize) return;
    const body = { prize_id: prize.id, game: gameKey };
    // Collages never attach to a player; regular prizes may
    if (!prizeIsCollage(prize) && !prizeIsWheel(prize) && selectedPlayer) body.session_id = selectedPlayer.session_id;
    return act('show', body);
  };
  const awardPrize = async () => {
    if (!selectedPlayer || !selectedPrize || selectedIsCollage || busy) return;
    const player = selectedPlayer;
    const prize = selectedPrize;
    const body = openShow && Number(openShow.prize_id) === Number(prize.id)
      ? { award_id: openShow.id, game: gameKey }
      : { prize_id: prize.id, session_id: player.session_id, game: gameKey };
    setPlayerId('');
    setPrizeId(null);
    const ok = await act('award', body);
    if (!ok) {
      setPlayerId((current) => current || player.session_id);
      setPrizeId((current) => (current == null ? prize.id : current));
    }
  };
  const releaseShow = () => { if (openShow) act('cancel', { award_id: openShow.id }); };
  const hidePopup = () => act('screen/hide', {});

  const toggleShowHide = async (prize) => {
    if (!prize || busy) return;
    setPrizeId(prize.id);
    if (isPrizeOnScreen(prize)) {
      await hidePopup();
    } else {
      await showPrize(prize);
    }
  };

  const handlePrizeTap = (prize) => {
    const now = Date.now();
    const last = lastTapRef.current;
    if (last.id === prize.id && now - last.at < 380) {
      lastTapRef.current = { id: null, at: 0 };
      toggleShowHide(prize);
      return;
    }
    lastTapRef.current = { id: prize.id, at: now };
    setPrizeId(prize.id);
  };

  const openFinder = () => {
    setFinderStep('name');
    setFinderName('');
    setFinderConfirm('');
    setFinderMatches([]);
    setFinderOpen(true);
  };

  const openPlayerPicker = () => {
    setPlayerPickerOpen(true);
  };

  const togglePlayer = (sessionId) => {
    setPlayerId((prev) => (prev === sessionId ? '' : sessionId));
  };

  const clearPlayer = () => setPlayerId('');

  const searchByName = async () => {
    if (!finderName.trim()) { setError('Enter a name'); return; }
    try {
      setFinderBusy(true); setError(null);
      const res = await fetch(`${API_BASE}/api/prize-palette/players/search?name=${encodeURIComponent(finderName.trim())}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) { setError(data.error || 'Search failed'); return; }
      setFinderMatches(data.matches || []);
      if (!data.matches?.length) { setError('No one in the venue matches that name'); return; }
      setFinderStep('confirm');
    } catch (err) { console.error('player search', err); setError('Search failed'); }
    finally { setFinderBusy(false); }
  };

  const screenToken = liveScreen?.token || '';
  useEffect(() => {
    if (!screenToken || !previewRef.current) return;
    previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [screenToken]);

  const confirmPlayer = async () => {
    if (!finderConfirm.trim()) { setError('Enter their email or Guest ID'); return; }
    try {
      setFinderBusy(true); setError(null);
      const res = await fetch(`${API_BASE}/api/prize-palette/players/confirm`, {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ name: finderName.trim(), confirm: finderConfirm.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) { setError(data.error || 'Could not confirm that player'); return; }
      setPlayerId(data.player.session_id);
      setFinderOpen(false);
      setPlayerPickerOpen(false);
    } catch (err) { console.error('confirm player', err); setError('Could not confirm that player'); }
    finally { setFinderBusy(false); }
  };

  usePrizePaletteEventAudio(liveScreen, { audible: true, ready: paletteAudioReady });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 12 }}>
        <CircularProgress sx={{ color: '#FFD54F' }} size={40} />
        <Typography sx={{ ml: 2, color: '#999' }}>Loading live desk…</Typography>
      </Box>
    );
  }

  const canShow = selectedPrize && !selectedIsWheel && prizeInStock(selectedPrize, openShow);
  const canAward = !selectedIsCollage && !selectedIsWheel && selectedPlayer && selectedPrize && prizeInStock(selectedPrize, openShow);
  const canStartWheel = selectedIsWheel && selectedPlayer && selectedPrize;
  const isReshow = !selectedIsCollage && !selectedIsWheel && openShow && selectedPrize && Number(openShow.prize_id) === Number(selectedPrize.id);
  const selectedOnScreen = isPrizeOnScreen(selectedPrize);
  const showHostPopup = Boolean(liveScreen) && !(wheelModalOpen && liveScreen.kind === 'wheel');

  return (
    <Box>
      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2, '& .MuiAlert-message': { fontWeight: 600 } }} onClose={() => setError(null)}>
            {error}
          </Alert>
        </Fade>
      )}

      {/* ── control bar (collapsible) ───────────────────────────── */}
      <Box sx={{
        mb: 2, borderRadius: 3,
        bgcolor: '#141414', border: '1px solid #2a2a2a',
        position: 'sticky', top: 0, zIndex: 8,
        backdropFilter: 'blur(12px)',
        overflow: 'hidden',
      }}>
        <Box
          onClick={() => setTopBarOpen((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 1,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>
                Popup target
              </Typography>
              <Chip
                label={prizeGameLabel(gameKey)}
                size="small"
                sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: '#FFD54F', color: '#111' }}
              />
              <Typography sx={{ color: '#555', fontSize: 11 }}>
                {topBarOpen ? 'Tap to collapse' : 'Tap to set game'}
              </Typography>
            </Box>
          </Box>
          <Tooltip title={liveScreen ? 'Hide the popup from all players' : 'No popup on screen'}>
            <span onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                variant="contained"
                startIcon={<HideIcon sx={{ fontSize: 16 }} />}
                disabled={busy || !liveScreen}
                onClick={hidePopup}
                sx={{
                  bgcolor: liveScreen ? '#fff' : '#1e1e1e',
                  color: liveScreen ? '#111' : '#555',
                  fontWeight: 800,
                  borderRadius: 2,
                  minWidth: 72,
                  px: 1.25,
                  py: 0.4,
                  fontSize: 12,
                  boxShadow: 'none',
                  '&.Mui-disabled': { bgcolor: '#1e1e1e', color: '#444' },
                  '&:hover': { bgcolor: liveScreen ? '#eee' : '#1e1e1e' },
                }}
              >
                Hide
              </Button>
            </span>
          </Tooltip>
          <IconButton size="small" sx={{ color: '#888' }} aria-label={topBarOpen ? 'Collapse target bar' : 'Expand target bar'}>
            {topBarOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* Always visible: which popup is currently active */}
        <Box sx={{ px: 1.5, pb: 1.25 }}>
          {liveScreen ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              bgcolor: '#1a1800',
              border: '1px solid #332',
            }}>
              <DotIcon sx={{
                fontSize: 10,
                color: liveScreen.status === 'awarded' ? '#81C784' : '#FFD54F',
                flexShrink: 0,
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } },
              }} />
              <Typography variant="body2" sx={{ color: '#FFD54F', fontWeight: 700, fontSize: 13, minWidth: 0 }}>
                {liveScreen.kind === 'collage'
                  ? <>Collage on screen: <strong>{liveScreen.prize_name || 'collage'}</strong> · {prizeGameLabel(liveScreen.game) || 'All'}</>
                  : liveScreen.kind === 'display'
                    ? <>Showing: <strong>{liveScreen.prize_name || 'a prize'}</strong> · {prizeGameLabel(liveScreen.game) || 'All'}</>
                    : <>
                      {liveScreen.status === 'awarded' ? 'Awarded' : 'Showing'}{' '}
                      <strong>{liveScreen.prize_name || 'a prize'}</strong>
                      {' '}to {liveScreen.winner_name || 'a player'}
                      {' '}· {prizeGameLabel(liveScreen.game) || 'All'}
                    </>}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#555', fontSize: 12, px: 0.5 }}>
              No popup on screen right now
            </Typography>
          )}
        </Box>

        <Collapse in={topBarOpen} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, pb: 2, pt: 0.5, borderTop: '1px solid #222' }}>
            <Typography variant="body2" sx={{ color: '#777', fontSize: 13, lineHeight: 1.5, mb: 1.25 }}>
              {gameKey === 'all' ? 'Shows on every player page.' : `Only inside ${prizeGameLabel(gameKey)}.`}
              {showTime ? ` Show: ${showTime.name}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {PRIZE_GAMES.map((game) => {
                const sel = gameKey === game.id;
                return (
                  <Chip key={game.id} label={game.label} size="small"
                    onClick={() => setGameKey(game.id)}
                    sx={{
                      fontWeight: 700, fontSize: 12,
                      bgcolor: sel ? '#FFD54F' : '#1e1e1e', color: sel ? '#111' : '#aaa',
                      border: sel ? 'none' : '1px solid #333',
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: sel ? '#FFD54F' : '#2a2a2a' },
                    }}
                  />
                );
              })}
            </Box>
          </Box>
        </Collapse>
      </Box>

      {showHostPopup ? (
        <Box ref={previewRef}>
          <PrizeRevealOverlay
            variant="inline"
            forcedScreen={liveScreen}
            onHide={hidePopup}
            hideBusy={busy}
          />
        </Box>
      ) : null}

      {/* ── prize grid (full width) ─────────────────────────────── */}
      <Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip label="All" size="small" onClick={() => setCategoryFilter('all')}
            sx={{ fontWeight: 700, bgcolor: categoryFilter === 'all' ? '#FFD54F' : '#1e1e1e', color: categoryFilter === 'all' ? '#111' : '#aaa', border: categoryFilter === 'all' ? 'none' : '1px solid #333' }} />
          {categories.map((cat) => (
            <Chip key={cat.id} label={cat.name} size="small"
              onClick={() => setCategoryFilter(String(cat.id))}
              sx={{
                fontWeight: 700,
                bgcolor: String(categoryFilter) === String(cat.id) ? (cat.color || '#FFD54F') : '#1e1e1e',
                color: String(categoryFilter) === String(cat.id) ? '#111' : '#aaa',
                border: String(categoryFilter) === String(cat.id) ? 'none' : '1px solid #333',
              }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          <Chip label="All values" size="small" onClick={() => setTierCap(null)}
            sx={{ fontWeight: 700, bgcolor: tierCap == null ? '#FFD54F' : '#1e1e1e', color: tierCap == null ? '#111' : '#aaa', border: tierCap == null ? 'none' : '1px solid #333' }} />
          {tiers.map((tier) => (
            <Chip key={tier.id} label={tier.label} size="small"
              onClick={() => setTierCap(Number(tier.cap_amount))}
              sx={{
                fontWeight: 700,
                bgcolor: Number(tierCap) === Number(tier.cap_amount) ? '#FFD54F' : '#1e1e1e',
                color: Number(tierCap) === Number(tier.cap_amount) ? '#111' : '#aaa',
                border: Number(tierCap) === Number(tier.cap_amount) ? 'none' : '1px solid #333',
              }} />
          ))}
          <TextField size="small" placeholder="Search prizes…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 160, ml: 'auto',
              '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#1a1a1a', '& fieldset': { borderColor: '#2a2a2a' }, '&:hover fieldset': { borderColor: '#444' } },
            }}
            InputProps={{ sx: { color: '#fff', fontSize: 13, height: 34 } }} />
        </Box>

        <Typography sx={{ color: '#555', fontSize: 11, mb: 1 }}>
          Double-tap a card to Show or Hide it on players&apos; screens
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
          gap: 1,
          alignItems: 'start',
        }}>
          {prizes.map((prize) => {
            const sel = prize.id === prizeId;
            const collage = prizeIsCollage(prize);
            const wheel = prizeIsWheel(prize);
            const value = prizeValueLabel(prize);
            const inStock = collage || wheel || prizeIsUnlimited(prize) || prize.available > 0;
            const onScreen = isPrizeOnScreen(prize);
            return (
              <Grow in key={prize.id} timeout={300} style={{ transformOrigin: '50% 0' }}>
                <Card
                  onClick={() => handlePrizeTap(prize)}
                  title="Tap to select · Double-tap to Show / Hide"
                  sx={{
                    bgcolor: '#141414',
                    borderRadius: 1.5,
                    border: '1.5px solid',
                    borderColor: onScreen ? '#81C784' : sel ? (wheel ? '#CE93D8' : collage ? '#90CAF9' : '#FFD54F') : 'transparent',
                    cursor: 'pointer',
                    opacity: inStock ? 1 : 0.4,
                    transition: 'all 0.2s ease',
                    height: 'auto',
                    alignSelf: 'start',
                    overflow: 'hidden',
                    transform: sel || onScreen ? 'scale(1.02)' : 'scale(1)',
                    boxShadow: onScreen
                      ? '0 4px 16px rgba(129,199,132,0.2)'
                      : sel
                        ? (wheel ? '0 4px 16px rgba(206,147,216,0.15)' : collage ? '0 4px 16px rgba(144,202,249,0.15)' : '0 4px 16px rgba(255,213,79,0.15)')
                        : '0 1px 4px rgba(0,0,0,0.3)',
                    '&:hover': {
                      borderColor: onScreen ? '#81C784' : sel ? (wheel ? '#CE93D8' : collage ? '#90CAF9' : '#FFD54F') : '#444',
                      transform: 'scale(1.02)',
                    },
                  }}
                >
                  {prize.image_url ? (
                    <CardMedia
                      component="img"
                      image={mediaUrl(prize.image_url)}
                      alt={prize.name}
                      sx={{
                        width: '100%',
                        aspectRatio: collage || wheel ? '1 / 1' : '3 / 1',
                        objectFit: 'cover',
                        objectPosition: 'center',
                        display: 'block',
                        borderBottom: collage ? '1px solid #1e3a5f' : 'none',
                      }}
                    />
                  ) : (
                    <Box sx={{
                      aspectRatio: collage || wheel ? '1 / 1' : '3 / 1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#1a1a1a',
                    }}>
                      <TrophyIcon sx={{ color: prize.category_color || (collage ? '#90CAF9' : '#FFD54F'), fontSize: 22, opacity: 0.6 }} />
                    </Box>
                  )}
                  <Box sx={{ px: 0.75, py: 0.75, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.35, flexWrap: 'wrap' }}>
                      <Typography sx={{
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: 11,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '100%',
                      }}>
                        {prize.name}
                      </Typography>
                      {collage ? (
                        <Chip label="Collage" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 800, bgcolor: '#1e3a5f', color: '#90CAF9' }} />
                      ) : wheel ? (
                        <Chip label="Wheel" size="small" sx={{ height: 16, fontSize: 9, fontWeight: 800, bgcolor: '#2e1e3f', color: '#CE93D8' }} />
                      ) : null}
                    </Box>
                    {!collage && (value || prize.tier_label) ? (
                      <Typography sx={{ color: '#FFD54F', fontSize: 10, fontWeight: 600, mt: 0.2 }}>
                        {value}{prize.tier_label ? ` · ${prize.tier_label}` : ''}
                      </Typography>
                    ) : null}
                    {onScreen ? (
                      <Typography sx={{ color: '#81C784', fontSize: 9, fontWeight: 700, mt: 0.2 }}>
                        On screen
                      </Typography>
                    ) : null}
                  </Box>
                </Card>
              </Grow>
            );
          })}
        </Box>
        {prizes.length === 0 && (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <TrophyIcon sx={{ fontSize: 40, color: '#333', mb: 1 }} />
            <Typography sx={{ color: '#555', fontSize: 14 }}>No prizes match these filters</Typography>
          </Box>
        )}
      </Box>

      {/* ── sticky action bar (collapsible) ─────────────────────── */}
      <Box sx={{
        position: 'sticky', bottom: 12, zIndex: 8, mt: 3,
        borderRadius: 3,
        bgcolor: '#141414', border: '1px solid #2a2a2a',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 -8px 30px rgba(0,0,0,0.4)',
        overflow: 'hidden',
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          flexWrap: 'wrap',
        }}>
          <Box
            onClick={() => setBottomBarOpen((v) => !v)}
            sx={{
              flex: 1,
              minWidth: 140,
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              cursor: 'pointer',
              py: 0.25,
            }}
          >
            <IconButton size="small" sx={{ color: '#888', p: 0.25 }} aria-label={bottomBarOpen ? 'Collapse award bar' : 'Expand award bar'}>
              {bottomBarOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{
                color: '#fff', fontWeight: 800, fontSize: 13,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {selectedPrize ? selectedPrize.name : 'Select a prize'}
                {selectedPlayer && !selectedIsCollage ? ` → ${selectedPlayer.username || 'Player'}` : ''}
              </Typography>
              <Typography sx={{ color: '#666', fontSize: 11 }}>
                {selectedIsWheel
                  ? (selectedPlayer ? 'Ready to spin' : 'Select player to spin')
                  : selectedIsCollage
                    ? 'Collage · Show / Hide'
                    : selectedPlayer
                      ? 'Ready to award'
                      : 'Tap to pick player / details'}
              </Typography>
            </Box>
          </Box>

          {selectedIsWheel ? (
            <Button
              size="small"
              variant="contained"
              startIcon={<SpinIcon sx={{ fontSize: 16 }} />}
              disabled={busy || wheelBusy || !canStartWheel}
              onClick={() => openWheelModal(selectedPrize, selectedPlayer)}
              sx={{
                bgcolor: '#CE93D8', color: '#111', fontWeight: 900, borderRadius: 2, px: 1.75,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#BA68C8' },
              }}
            >
              Start Wheel
            </Button>
          ) : (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={selectedOnScreen ? <HideIcon sx={{ fontSize: 16 }} /> : <ShowIcon sx={{ fontSize: 16 }} />}
                disabled={busy || (!selectedOnScreen && !canShow)}
                onClick={() => (selectedOnScreen ? hidePopup() : showPrize())}
                sx={{
                  color: selectedOnScreen ? '#81C784' : '#FFD54F',
                  borderColor: selectedOnScreen ? '#81C78466' : '#FFD54F44',
                  fontWeight: 800, borderRadius: 2, px: 1.5,
                  '&:hover': {
                    borderColor: selectedOnScreen ? '#81C784' : '#FFD54F',
                    bgcolor: selectedOnScreen ? 'rgba(129,199,132,0.08)' : 'rgba(255,213,79,0.08)',
                  },
                }}
              >
                {selectedOnScreen ? 'Hide' : 'Show'}
              </Button>
              {!selectedIsCollage ? (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AwardIcon sx={{ fontSize: 16 }} />}
                  disabled={busy || !canAward}
                  onClick={awardPrize}
                  sx={{
                    bgcolor: '#FFD54F', color: '#111', fontWeight: 900, borderRadius: 2, px: 1.75,
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#FFC107' },
                  }}
                >
                  Award
                </Button>
              ) : null}
            </>
          )}
        </Box>

        <Collapse in={bottomBarOpen} timeout="auto" unmountOnExit>
          <Box sx={{
            px: 2, pb: 2, pt: 1.25,
            borderTop: '1px solid #222',
            display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start',
          }}>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 14 }}>
                  {selectedPrize ? selectedPrize.name : 'Select a prize'}
                </Typography>
                {selectedIsCollage ? (
                  <Chip label="Collage · Show / Hide only" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: '#1e3a5f', color: '#90CAF9' }} />
                ) : selectedIsWheel ? (
                  <Chip label="Wheel · Select player & spin" size="small" sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: '#2e1e3f', color: '#CE93D8' }} />
                ) : null}
              </Box>

              {!selectedIsCollage ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {selectedIsWheel && !selectedPlayer ? (
                    <Typography sx={{ color: '#CE93D8', fontSize: 13, fontWeight: 700 }}>
                      Select a player below, then click &quot;Start Wheel&quot;
                    </Typography>
                  ) : null}
                  <Box
                    onClick={openPlayerPicker}
                    sx={{
                      flex: 1,
                      minWidth: 200,
                      maxWidth: 360,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.25,
                      py: 1,
                      borderRadius: 2,
                      bgcolor: '#1a1a1a',
                      border: '1px solid',
                      borderColor: selectedPlayer ? '#FFD54F' : '#333',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s',
                      '&:hover': { borderColor: selectedPlayer ? '#FFD54F' : '#555' },
                    }}
                  >
                    <Avatar sx={{
                      width: 28, height: 28,
                      bgcolor: selectedPlayer ? '#FFD54F' : '#2a2a2a',
                      color: selectedPlayer ? '#111' : '#888',
                      fontSize: 13, fontWeight: 800,
                    }}>
                      {selectedPlayer ? (selectedPlayer.username || 'P')[0].toUpperCase() : <PersonIcon sx={{ fontSize: 16 }} />}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: selectedPlayer ? '#fff' : '#777', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {selectedPlayer ? (selectedPlayer.username || 'Player') : 'Select player'}
                      </Typography>
                      {selectedPlayer ? (
                        <Typography sx={{ color: '#666', fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {selectedPlayer.current_page_name || 'Home'}
                          {selectedPlayer.guest_id ? ` · ${selectedPlayer.guest_id}` : ''}
                        </Typography>
                      ) : (
                        <Typography sx={{ color: '#555', fontSize: 11 }}>
                          {players.length} in venue · required to Award
                        </Typography>
                      )}
                    </Box>
                    <ExpandMoreIcon sx={{ color: '#666', fontSize: 20 }} />
                  </Box>

                  {selectedPlayer ? (
                    <Button
                      size="small"
                      onClick={clearPlayer}
                      sx={{ color: '#aaa', textTransform: 'none', fontSize: 12, minWidth: 0, px: 1 }}
                    >
                      Clear
                    </Button>
                  ) : null}
                </Box>
              ) : (
                <Typography sx={{ color: '#777', fontSize: 13 }}>
                  No player needed — double-tap the card or use Show / Hide
                </Typography>
              )}

              <Typography sx={{ color: '#666', fontSize: 12, mt: 0.75 }}>
                {selectedIsWheel
                  ? (selectedPlayer ? 'Start Wheel opens the spin wheel for this player' : 'Select a player first, then Start Wheel')
                  : selectedIsCollage
                    ? (selectedOnScreen ? 'Collage is live — Hide or double-tap again to clear' : 'Show puts the collage on every player screen')
                    : isReshow
                      ? 'Already on their screen — Award makes it official'
                      : selectedPlayer
                        ? 'Show opens the surprise · Award writes the record · Double-tap card to toggle Show/Hide'
                        : 'Show displays the prize to everyone · Select a player to Award · Double-tap card to toggle'}
              </Typography>
              <Typography sx={{ color: '#FFD54F', fontSize: 12, fontWeight: 700 }}>
                {prizeGameLabel(gameKey)}
              </Typography>
            </Box>

            {!selectedIsCollage && !selectedIsWheel ? (
              <Button variant="text" startIcon={<CloseIcon sx={{ fontSize: 16 }} />}
                disabled={busy || !openShow} onClick={releaseShow}
                sx={{ color: '#f44336', fontWeight: 700, fontSize: 13, borderRadius: 2, alignSelf: 'center' }}>
                Release
              </Button>
            ) : null}
          </Box>
        </Collapse>
      </Box>

      {/* ── wheel prize modal ──────────────────────────────────── */}
      <Dialog
        open={wheelModalOpen}
        onClose={() => {}}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { bgcolor: '#0e0e0c', color: '#fff', borderRadius: 3, border: '1px solid #2a2a2a', minHeight: 520 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpinIcon sx={{ color: '#CE93D8' }} />
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
                {selectedPrize?.name || 'Wheel'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#888' }}>
                Playing for: {selectedPlayer?.username || wheelSession?.player_name || 'Player'}
                {wheelMeta?.wheel_name ? ` · ${wheelMeta.wheel_name}` : ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {wheelSession && !wheelSpinning && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={wheelPlayerCanSpin}
                    disabled={wheelBusy || wheelSpinning}
                    onChange={(e) => patchWheelSettings({ player_can_spin: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#CE93D8' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#CE93D8' },
                    }}
                  />
                }
                label={<Typography variant="caption" sx={{ color: '#aaa' }}>Player spins</Typography>}
              />
            )}
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<EndIcon sx={{ fontSize: 16 }} />}
              disabled={wheelBusy || wheelSpinning}
              onClick={closeWheelModal}
            >
              {wheelWinner ? 'Save & Close' : 'Close'}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' }, pt: 1 }}>
          {/* Wheel display */}
          <Box sx={{ flex: 1, width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            {wheelEntries.length > 0 ? (
              <StableLiveWheel
                entries={wheelEntries}
                spinning={wheelSpinning}
                finalAngle={wheelFinalAngle}
                spinDuration={wheelSpinDuration}
                winnerEntry={wheelWinner}
                animationComplete={wheelAnimComplete}
                onAnimationEnd={handleWheelAnimEnd}
                size={320}
                centerLogoSrc="/mind_your_music.png"
                hotSeatPlayerName={selectedPlayer?.username || wheelSession?.player_name || null}
                backgroundImageUrl={wheelMeta?.background_image_url || ''}
                variant="default"
                prizeIndex={wheelPrizeIndex}
                suspenseSpin
                revealDelayMs={2000}
                onLanded={markWheelLanded}
                onWinnerReveal={revealWheelPrize}
              />
            ) : (
              <Box sx={{ py: 8 }}>
                <CircularProgress sx={{ color: '#CE93D8' }} />
              </Box>
            )}

            {/* Spin controls */}
            <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25 }}>
              {wheelPlayerCanSpin ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: '#aaa', fontSize: 13 }}>
                    Player spin is on. Unlock to give one spin.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                      size="small"
                      icon={wheelPlayerUnlocked ? <LockOpenIcon /> : <LockIcon />}
                      label={wheelPlayerUnlocked ? 'Unlocked — one spin' : 'Locked'}
                      color={wheelPlayerUnlocked ? 'success' : 'default'}
                      variant={wheelPlayerUnlocked ? 'filled' : 'outlined'}
                    />
                    {wheelPlayerUnlocked ? (
                      <Button size="small" variant="outlined" startIcon={<LockIcon />}
                        disabled={wheelBusy} onClick={() => patchWheelSettings({ player_can_spin: true, player_spin_unlocked: false })}>
                        Lock
                      </Button>
                    ) : (
                      <Button size="small" variant="contained" color="success" startIcon={<LockOpenIcon />}
                        disabled={wheelBusy} onClick={() => patchWheelSettings({ player_can_spin: true, player_spin_unlocked: true })}>
                        Unlock
                      </Button>
                    )}
                  </Box>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={wheelSpinning ? <CircularProgress size={18} color="inherit" /> : <SpinIcon />}
                  disabled={wheelBusy || wheelSpinning || wheelEntries.length < 2}
                  onClick={handleWheelSpin}
                  sx={{
                    background: wheelSpinning ? undefined : 'linear-gradient(135deg, #CE93D8, #FFD54F)',
                    fontWeight: 700,
                    px: 5,
                    py: 1.2,
                    color: '#111',
                  }}
                >
                  {wheelSpinning ? (wheelHold ? 'Here it comes…' : 'Spinning…') : 'SPIN'}
                </Button>
              )}
            </Box>
          </Box>

          {/* Right panel: entries + result */}
          <Box sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Slices ({wheelEntries.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2, maxHeight: 220, overflow: 'auto' }}>
              {wheelEntries.map((e) => (
                <Chip
                  key={e.id}
                  label={e.display_name || 'Prize'}
                  size="small"
                  sx={{
                    bgcolor: e.slice_color,
                    color: '#fff',
                    fontWeight: e.is_winner ? 800 : 400,
                    border: e.is_winner ? '2px solid #FFD700' : 'none',
                  }}
                />
              ))}
            </Box>

            {wheelWinner && !wheelSpinning && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>
                  Result: {wheelWinner.display_name || 'Prize'}
                </Typography>
                {wheelWinner.points > 0 ? (
                  <Typography variant="caption">+{wheelWinner.points} pts</Typography>
                ) : null}
                <Typography variant="body2" sx={{ mt: 0.5, color: '#666', fontSize: 12 }}>
                  Click &quot;Save &amp; Close&quot; to record this result.
                </Typography>
              </Alert>
            )}

            {wheelSession && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ color: '#666' }}>
                  Spin count: {wheelSession.spin_count || 0}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── player picker modal ─────────────────────────────────── */}
      <Dialog
        open={playerPickerOpen}
        onClose={() => setPlayerPickerOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#161616', color: '#fff', borderRadius: 3, border: '1px solid #2a2a2a' } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={players.length} color="warning" max={99} sx={{ '& .MuiBadge-badge': { bgcolor: '#FFD54F', color: '#111', fontWeight: 800 } }}>
              <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Select player</Typography>
            </Badge>
          </Box>
          <Button size="small" startIcon={<SearchIcon sx={{ fontSize: 16 }} />} onClick={openFinder}
            sx={{ color: '#FFD54F', textTransform: 'none', fontWeight: 700, fontSize: 13, borderRadius: 2 }}>
            Find by name
          </Button>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Filter by name, email, Guest ID…"
            value={playerFilter}
            onChange={(e) => setPlayerFilter(e.target.value)}
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#1a1a1a', '& fieldset': { borderColor: '#2a2a2a' } },
            }}
            InputProps={{ sx: { color: '#fff', fontSize: 13 } }}
          />
          <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 0.75,
            maxHeight: 420, overflow: 'auto', pr: 0.5,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#333', borderRadius: 4 },
          }}>
            {filteredPlayers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <PersonIcon sx={{ fontSize: 40, color: '#333', mb: 1 }} />
                <Typography sx={{ color: '#555', fontSize: 13 }}>
                  {players.length === 0 ? 'No players in the venue' : 'No players match this filter'}
                </Typography>
              </Box>
            ) : filteredPlayers.map((player) => {
              const showing = shownAwards.find((a) => a.session_id === player.session_id);
              const sel = player.session_id === playerId;
              const isAway = player.status === 'away' || player.status === 'idle';
              return (
                <Box
                  key={player.session_id}
                  onClick={() => togglePlayer(player.session_id)}
                  title={sel ? 'Click again to unselect' : 'Select player'}
                  sx={{
                    p: 1.25, borderRadius: 2, cursor: 'pointer',
                    bgcolor: sel ? '#1f1b10' : '#111',
                    border: '1px solid', borderColor: sel ? '#FFD54F' : '#222',
                    opacity: isAway ? 0.7 : 1,
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: sel ? '#1f1b10' : '#1a1a1a', borderColor: sel ? '#FFD54F' : '#333' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: sel ? '#FFD54F' : '#2a2a2a', color: sel ? '#111' : '#888', fontSize: 13, fontWeight: 800 }}>
                      {(player.username || 'P')[0].toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {player.username || 'Player'}
                        {isAway ? (
                          <Typography component="span" sx={{ color: '#888', fontWeight: 600, fontSize: 11, ml: 0.75 }}>
                            Away
                          </Typography>
                        ) : null}
                      </Typography>
                      <Typography sx={{ color: '#666', fontSize: 11 }}>
                        {player.current_page_name || 'Home'}
                        {player.guest_id ? ` · ${player.guest_id}` : ''}
                      </Typography>
                    </Box>
                    {sel ? (
                      <Chip label="Selected" size="small" sx={{ bgcolor: '#FFD54F', color: '#111', fontWeight: 800, fontSize: 11, height: 22 }} />
                    ) : null}
                    {showing && !sel ? <DotIcon sx={{ fontSize: 8, color: '#FFD54F' }} /> : null}
                  </Box>
                  {player.email && (
                    <Typography sx={{ color: '#555', fontSize: 11, mt: 0.5, ml: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {player.email}
                    </Typography>
                  )}
                  {showing && (
                    <Typography sx={{ color: '#FFD54F', fontSize: 11, mt: 0.25, ml: 5, fontWeight: 600 }}>
                      {showing.popup_hidden ? 'Hidden' : 'On screen'}: {showing.prize_name}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          {selectedPlayer ? (
            <Button onClick={clearPlayer} sx={{ color: '#aaa', borderRadius: 2, mr: 'auto' }}>
              Unselect
            </Button>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}
          <Button onClick={() => setPlayerPickerOpen(false)} sx={{ color: '#666', borderRadius: 2 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => setPlayerPickerOpen(false)}
            disabled={!selectedPlayer}
            sx={{ bgcolor: '#FFD54F', color: '#111', fontWeight: 800, borderRadius: 2 }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── finder dialog ───────────────────────────────────────── */}
      <Dialog open={finderOpen} onClose={() => setFinderOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { bgcolor: '#161616', color: '#fff', borderRadius: 3, border: '1px solid #2a2a2a' } }}>
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Find a player</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#777', mb: 2, fontSize: 13 }}>
            Search by name first, then confirm with email or Guest ID.
          </Typography>
          <TextField fullWidth label="Name" value={finderName}
            onChange={(e) => { setFinderName(e.target.value); setFinderStep('name'); setFinderMatches([]); }}
            sx={{ mb: 2 }}
            InputLabelProps={{ sx: { color: '#666' } }}
            InputProps={{ sx: { color: '#fff', borderRadius: 2 } }} />
          {finderStep === 'confirm' && (
            <>
              <Typography variant="body2" sx={{ color: '#999', mb: 1, fontSize: 13 }}>
                {finderMatches.length} found in the venue
              </Typography>
              <Box sx={{ mb: 2, maxHeight: 160, overflow: 'auto', borderRadius: 2, border: '1px solid #2a2a2a', bgcolor: '#111' }}>
                {finderMatches.map((match) => (
                  <Box key={match.session_id} sx={{ px: 1.5, py: 1, borderBottom: '1px solid #222', '&:last-child': { borderBottom: 'none' } }}>
                    <Typography sx={{ color: '#FFD54F', fontWeight: 700, fontSize: 13 }}>
                      {match.display_name} · {match.account === 'guest' ? 'Guest' : 'Member'}
                    </Typography>
                    <Typography sx={{ color: '#777', fontSize: 12 }}>
                      {match.email || ''}{match.guest_id ? ` · ${match.guest_id}` : ''}
                      {match.current_page_name ? ` · ${match.current_page_name}` : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
              <TextField fullWidth label="Email or Guest ID" value={finderConfirm}
                onChange={(e) => setFinderConfirm(e.target.value)}
                InputLabelProps={{ sx: { color: '#666' } }}
                InputProps={{ sx: { color: '#fff', borderRadius: 2 } }} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFinderOpen(false)} sx={{ color: '#666', borderRadius: 2 }}>Cancel</Button>
          {finderStep === 'name' ? (
            <Button onClick={searchByName} disabled={finderBusy} variant="contained"
              sx={{ bgcolor: '#FFD54F', color: '#111', fontWeight: 800, borderRadius: 2 }}>
              {finderBusy ? 'Searching…' : 'Search'}
            </Button>
          ) : (
            <Button onClick={confirmPlayer} disabled={finderBusy} variant="contained"
              sx={{ bgcolor: '#FFD54F', color: '#111', fontWeight: 800, borderRadius: 2 }}>
              {finderBusy ? 'Checking…' : 'Confirm'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrizeLiveAward;
